import { defineStore } from 'pinia';

export const usePFStore = defineStore('pf', {
  state: () => ({
    dadosPF: null,
    isLoading: false,
    error: null,
    saldoTotal: 0,
    saldoOperacional: 0,
    saldoDepositoPrevio: 0,
    transacoes: [],
    statusContaPix: null,
    // Dados para saque
    valorSaque: null,
    valorSaqueNumerico: 0,
    dadosSaque: { nome: '', cpf: '' },
    // Dados para boleto
    valorBoleto: null,
    valorBoletoNumerico: 0
  }),

  getters: {
    cpf: (state) => state.dadosPF?.cpf || null,
    nomeCompleto: (state) => state.dadosPF?.nome || '',
    temContaPix: (state) => state.statusContaPix?.existe === true,
    contaPixAtiva: (state) => state.statusContaPix?.ativa === true,
    saldoTotalFormatado: (state) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(state.saldoTotal);
    },
    saldoOperacionalFormatado: (state) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(state.saldoOperacional);
    },
    saldoDepositoPrevioFormatado: (state) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(state.saldoDepositoPrevio);
    },
    valorSaqueFormatado: (state) => state.valorSaque || 'R$ 0,00',
    valorBoletoFormatado: (state) => state.valorBoleto || 'R$ 0,00'
  },

  actions: {
    /**
     * Define os dados da pessoa física
     */
    setDadosPF(dados) {
      this.dadosPF = dados;
      this.error = null;
    },

    /**
     * Define os saldos da conta
     */
    setSaldos({ saldoTotal, saldoOperacional, saldoDepositoPrevio }) {
      this.saldoTotal = saldoTotal || 0;
      this.saldoOperacional = saldoOperacional || 0;
      this.saldoDepositoPrevio = saldoDepositoPrevio || 0;
    },

    /**
     * Define as transações
     */
    setTransacoes(transacoes) {
      this.transacoes = transacoes || [];
    },

    /**
     * Define o status da conta PIX
     */
    setStatusContaPix(status) {
      this.statusContaPix = status;
    },

    /**
     * Define o estado de loading
     */
    setLoading(loading) {
      this.isLoading = loading;
    },

    /**
     * Define um erro
     */
    setError(error) {
      this.error = error;
      this.isLoading = false;
    },

    /**
     * Limpa todos os dados
     */
    limparDados() {
      this.dadosPF = null;
      this.saldoTotal = 0;
      this.saldoOperacional = 0;
      this.saldoDepositoPrevio = 0;
      this.transacoes = [];
      this.statusContaPix = null;
      this.error = null;
      this.isLoading = false;
    },

    /**
     * Carrega dados da pessoa física do storage
     */
    carregarDadosDoStorage() {
      try {
        const dadosPFData = localStorage.getItem('dadosPF');
        if (dadosPFData) {
          const dados = JSON.parse(dadosPFData);
          this.setDadosPF(dados);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Erro ao carregar dados PF do storage:', error);
        return false;
      }
    },

    /**
     * Salva dados da pessoa física no storage
     */
    salvarDadosNoStorage() {
      try {
        if (this.dadosPF) {
          localStorage.setItem('dadosPF', JSON.stringify(this.dadosPF));
        }
      } catch (error) {
        console.error('Erro ao salvar dados PF no storage:', error);
      }
    },

    /**
     * Valida se os dados PF são válidos
     */
    validarDadosPF(dados) {
      return dados && 
             typeof dados === 'object' && 
             dados.cpf && 
             typeof dados.cpf === 'string' && 
             dados.cpf.trim().length > 0 &&
             dados.nome && 
             typeof dados.nome === 'string';
    },

    /**
     * Verifica se o usuário tem permissão para operações PF
     */
    verificarPermissaoPF() {
      // Para PF, a permissão é baseada na existência do CPF
      return this.cpf && this.cpf.length === 11;
    },

    /**
     * Define valor e dados para saque
     */
    setValorSaque(valor, valorNumerico) {
      this.valorSaque = valor;
      this.valorSaqueNumerico = valorNumerico;
    },

    /**
     * Define dados do saque
     */
    setDadosSaque(nome, cpf) {
      this.dadosSaque = { nome, cpf };
    },

    /**
     * Limpa dados do saque
     */
    limparDadosSaque() {
      this.valorSaque = null;
      this.valorSaqueNumerico = 0;
      this.dadosSaque = { nome: '', cpf: '' };
    },

    /**
     * Define valor para boleto
     */
    setValorBoleto(valor, valorNumerico) {
      this.valorBoleto = valor;
      this.valorBoletoNumerico = valorNumerico;
    },

    /**
     * Limpa dados do boleto
     */
    limparDadosBoleto() {
      this.valorBoleto = null;
      this.valorBoletoNumerico = 0;
    }
  }
});
